import { useEffect, useMemo, useState } from 'react';
import { useAdminApi } from '../../../lib/adminApi';
import { sendCampaignInBatches, describeSendResult } from '../../../lib/mailingSend';

// Delivery status per newsletter campaign; failed / not yet attempted addresses can be sent again.
// The email text is not stored – subject and HTML are entered again for every resend.

const STATUS_LABEL = { sent: 'Gesendet', failed: 'Fehlgeschlagen', pending: 'Offen' };
const STATUS_STYLE = {
  sent:    { background: '#e6f4ea', color: '#1e7e34' },
  failed:  { background: '#fdecea', color: '#b02a37' },
  pending: { background: '#fff4e5', color: '#8a5300' },
};
// Recommended portion per send – Gmail blocked the account after ~90 mails in a short time
const PORTION = 50;

const fmt = (d) => d
  ? new Date(d).toLocaleString('de-DE', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
  : '';

export default function MailingStatus({ refreshKey = 0 }) {
  const api = useAdminApi();

  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState('');
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('open'); // open = failed + pending
  const [search, setSearch] = useState('');
  const [sel, setSel] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(null);
  const [modal, setModal] = useState({ show: false, title: '', body: '' });
  const [reload, setReload] = useState(0);
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');

  // campaigns
  useEffect(() => {
    (async () => {
      setErr('');
      try {
        const list = await api.get('/mailings');
        setCampaigns(list || []);
        setCampaignId(cur => (cur && list?.some(c => c.id === cur)) ? cur : (list?.[0]?.id || ''));
        if (!list?.length) setLoading(false);
      } catch (e) {
        setErr(String(e?.message || e));
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, reload]);

  // recipients of the selected campaign
  useEffect(() => {
    if (!campaignId) return;
    (async () => {
      setLoading(true); setErr('');
      try {
        const data = await api.get(`/mailings/${encodeURIComponent(campaignId)}/recipients`);
        setRows(data || []);
        setSel(new Set());
      } catch (e) {
        setErr(String(e?.message || e));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, refreshKey, reload]);

  const campaign = campaigns.find(c => c.id === campaignId);

  // prefill the subject with the campaign's name (editable); the text has to be pasted again
  useEffect(() => {
    setSubject(campaign?.subject || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, campaign?.subject]);

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      if (filter === 'open' && r.status === 'sent') return false;
      if (['sent', 'failed', 'pending'].includes(filter) && r.status !== filter) return false;
      return !q || r.email.includes(q);
    });
  }, [rows, filter, search]);

  // only addresses that did not receive the mail can be selected
  const selectable = visibleRows.filter(r => r.status !== 'sent');
  const allChecked = selectable.length > 0 && selectable.every(r => sel.has(r.email));

  const toggle = (email) => {
    const next = new Set(sel);
    if (next.has(email)) next.delete(email); else next.add(email);
    setSel(next);
  };
  const toggleAll = () => {
    const next = new Set(sel);
    if (allChecked) selectable.forEach(r => next.delete(r.email));
    else selectable.forEach(r => next.add(r.email));
    setSel(next);
  };
  const selectPortion = () => setSel(new Set(selectable.slice(0, PORTION).map(r => r.email)));

  const resend = async () => {
    const emails = Array.from(sel);
    setSending(true);
    setProgress({ done: 0, total: emails.length });
    let result;
    try {
      result = await sendCampaignInBatches(api, { campaignId, subject, htmlBody, emails, onProgress: setProgress });
    } finally {
      setSending(false);
      setProgress(null);
    }
    const ok = !result.failed.length && !result.stopped && !result.abortError;
    setModal({
      show: true,
      title: ok ? 'E-Mail gesendet' : (result.sent ? 'Teilweise gesendet' : 'Nicht gesendet'),
      body: describeSendResult(result).replace(/\n\nFehlgeschlagene und noch offene.*$/s, ''),
    });
    setReload(k => k + 1);
  };

  if (!loading && !err && campaigns.length === 0) {
    return (
      <div className="text-center text-muted py-5">
        Noch keine Mailings versendet. Sobald Sie im Tab „Mailings“ eine E-Mail senden, erscheint hier der Versand-Status.
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      {sending && (
        <div aria-live="polite" aria-busy="true" style={{
          position: 'absolute', inset: 0, background: 'rgba(255,255,255,.7)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 10,
        }}>
          <div className="text-center">
            <div className="spinner-border brand-spinner" role="status" />
            <div className="small mt-2 text-muted">
              Sende E-Mails… bitte warten{progress ? ` (${progress.done} / ${progress.total})` : ''}
            </div>
          </div>
        </div>
      )}

      {/* Campaign + summary */}
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <label className="form-label m-0 me-2">Mailing</label>
        <select className="form-select" style={{ maxWidth: 520 }} value={campaignId}
                onChange={(e) => setCampaignId(e.target.value)}>
          {campaigns.map(c => (
            <option key={c.id} value={c.id}>{fmt(c.createdAt)} – {c.subject}</option>
          ))}
        </select>
      </div>

      {campaign && (
        <div className="d-flex flex-wrap gap-2 mb-3 small">
          <span className="badge-soft" style={STATUS_STYLE.sent}>Gesendet: {campaign.sent}</span>
          <span className="badge-soft" style={STATUS_STYLE.failed}>Fehlgeschlagen: {campaign.failed}</span>
          <span className="badge-soft" style={STATUS_STYLE.pending}>Offen: {campaign.pending}</span>
          {campaign.lastAttemptAt && <span className="text-muted ms-2">Letzter Versuch: {fmt(campaign.lastAttemptAt)}</span>}
        </div>
      )}

      {/* Filters */}
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <label className="form-label m-0 me-2">Status</label>
        <select className="form-select" style={{ maxWidth: 260 }} value={filter}
                onChange={(e) => { setFilter(e.target.value); setSel(new Set()); }}>
          <option value="open">Nicht erhalten (fehlgeschlagen + offen)</option>
          <option value="failed">Nur fehlgeschlagen</option>
          <option value="pending">Nur offen</option>
          <option value="sent">Gesendet</option>
          <option value="all">Alle</option>
        </select>
        <input type="search" className="form-control" style={{ maxWidth: 260 }} placeholder="Suche: E-Mail…"
               value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Suche nach E-Mail" />
        <div className="ms-auto small text-muted">Sichtbar: {visibleRows.length} • Gesamt: {rows.length}</div>
      </div>

      {err && <div className="alert alert-danger">{err}</div>}

      <div className="table-responsive" style={{ maxHeight: 420, overflowY: 'auto', border: '1px solid #eee', borderRadius: 8 }}>
        {loading ? (
          <div className="p-4 text-center">
            <div className="spinner-border brand-spinner" role="status" />
            <div className="small mt-2 text-muted">Lädt…</div>
          </div>
        ) : (
          <table className="table table-sm align-middle m-0">
            <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
              <tr>
                <th style={{ width: 32 }}>
                  <input type="checkbox" checked={allChecked} disabled={!selectable.length} onChange={toggleAll} />
                </th>
                <th>Email</th>
                <th>Status</th>
                <th>Fehler</th>
                <th>Versuche</th>
                <th>Letzter Versuch</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map(r => (
                <tr key={r.email}>
                  <td>
                    <input type="checkbox" checked={sel.has(r.email)} disabled={r.status === 'sent'}
                           onChange={() => toggle(r.email)} />
                  </td>
                  <td>{r.email}</td>
                  <td>
                    <span className="badge-soft" style={STATUS_STYLE[r.status]}>{STATUS_LABEL[r.status] || r.status}</span>
                  </td>
                  <td className="small text-muted" style={{ maxWidth: 360 }}>{r.error}</td>
                  <td>{r.attempts}</td>
                  <td className="small">{r.attempts ? fmt(r.status === 'sent' ? r.sentAt : r.lastAttemptAt) : ''}</td>
                </tr>
              ))}
              {!visibleRows.length && (
                <tr><td colSpan="6" className="text-center text-muted py-4">
                  {filter === 'open' ? 'Alle Empfänger haben diese E-Mail erhalten.' : 'Keine Einträge.'}
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-3">
        <div className="mb-2 small text-muted">Ausgewählt: {sel.size}</div>
        <div className="mb-2">
          <label className="form-label">Betreff</label>
          <input className="form-control" value={subject} onChange={(e) => setSubject(e.target.value)} />
        </div>
        <div className="mb-2">
          <label className="form-label">Nachricht (HTML erlaubt)</label>
          <textarea className="form-control" rows={6} value={htmlBody} onChange={(e) => setHtmlBody(e.target.value)}
                    placeholder="Den gleichen HTML-Text wie beim ursprünglichen Versand hier einfügen…" />
        </div>
      </div>

      <div className="mt-2 d-flex flex-wrap align-items-center gap-2">
        <button type="button" className="btn btn-sm btn-edit" disabled={!selectable.length || sending} onClick={selectPortion}>
          Erste {PORTION} auswählen
        </button>
        <button className="btn btn-save" disabled={sending || sel.size === 0 || !subject.trim() || !htmlBody.trim()} onClick={resend}>
          {sending ? (progress ? `Sende… ${progress.done} / ${progress.total}` : 'Sende…') : `Erneut senden (${sel.size})`}
        </button>
        <span className="small text-muted">
          Tipp: höchstens ca. {PORTION} E-Mails pro Durchgang und dazwischen etwa eine Stunde warten – sonst sperrt Gmail den Versand.
          Wer die E-Mail schon erhalten hat, bekommt sie nicht noch einmal.
        </span>
      </div>

      {modal.show && (
        <>
          <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{modal.title}</h5>
                  <button type="button" className="btn-close" aria-label="Close"
                          onClick={() => setModal({ show: false, title: '', body: '' })}></button>
                </div>
                <div className="modal-body">
                  <p style={{ whiteSpace: 'pre-line' }}>{modal.body}</p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-primary"
                          onClick={() => setModal({ show: false, title: '', body: '' })}>OK</button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  );
}
